import { useEffect } from "react";

// Referral capture and carry.
//
// www.ukvisajobs.com and my.ukvisajobs.com are separate origins, so a referral
// captured on the public site cannot reach this app through localStorage. It
// travels in a cookie scoped to the parent domain instead — the same mechanism
// authToken already uses — and localStorage is kept only as a same-origin
// fallback for when cookies are unavailable.

export const REF_COOKIE = "ukvj_ref";
export const CAMPAIGN_COOKIE = "ukvj_campaign";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90; // 90 days

// A query string value is absent as null, and both cookies and localStorage
// stringify whatever they are given, so an absent value becomes the literal
// "null" and destroys a referral captured on an earlier page.
export const cleanRefValue = (value) => {
  if (value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return "";
  return trimmed;
};

const cookieDomain = () => {
  const host = window.location.hostname;
  return host.endsWith("ukvisajobs.com") ? "; Domain=.ukvisajobs.com" : "";
};

const writeCookie = (name, value) => {
  try {
    document.cookie =
      `${name}=${encodeURIComponent(value)}` +
      `; Path=/${cookieDomain()}; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax` +
      (window.location.protocol === "https:" ? "; Secure" : "");
  } catch (e) {
    // cookies disabled — localStorage still holds it for this origin
  }
};

const readCookie = (name) => {
  try {
    const hit = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    return hit ? cleanRefValue(decodeURIComponent(hit.split("=")[1])) : "";
  } catch (e) {
    return "";
  }
};

const readStorage = (key) => {
  try {
    return cleanRefValue(localStorage.getItem(key));
  } catch (e) {
    return "";
  }
};

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // private mode — the cookie still carries it
  }
};

export const getStoredReferral = () => ({
  ref: readCookie(REF_COOKIE) || readStorage("ref"),
  compaign_name: readCookie(CAMPAIGN_COOKIE) || readStorage("compaign_name"),
});

// Stores a referral present in the current URL and then removes the
// parameters, so they are not re-shared or crawled. Idempotent.
export const captureReferral = () => {
  let params;
  try {
    params = new URL(window.location.href).searchParams;
  } catch (e) {
    return getStoredReferral();
  }

  const ref = cleanRefValue(params.get("ref"));
  const campaign = cleanRefValue(params.get("compaign_name"));

  if (ref) {
    writeCookie(REF_COOKIE, ref);
    writeStorage("ref", ref);
    if (campaign) {
      writeCookie(CAMPAIGN_COOKIE, campaign);
      writeStorage("compaign_name", campaign);
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("ref");
      url.searchParams.delete("compaign_name");
      window.history.replaceState(
        window.history.state,
        "",
        url.pathname + url.search + url.hash
      );
    } catch (e) {
      // replaceState unavailable — the capture already happened
    }
  }

  return getStoredReferral();
};

const useReferralLink = (ref, compaign_name) => {
  const incomingRef = cleanRefValue(ref);

  useEffect(() => {
    captureReferral();
  }, [incomingRef]);

  return incomingRef || getStoredReferral().ref;
};

export default useReferralLink;
